import { gql } from "@codesandbox/api";
import { Authorization } from "gql-types/schemas";
import {
  Box,
  Button,
  InputWithSuggestions,
  Text,
  Stack,
  Avatar,
} from "prism-react";
import { useEffect, useState } from "react";

import { useAnalytics } from "hooks/useAnalytics";

import { useShareContext } from "../ShareSandboxProvider";
import {
  useAddCollaborator,
  useInviteCollaborator,
  useSearchUsersByName,
} from "../api";

export const InviteForm = ({
  canInvite,
  refetchCollaborators,
}: {
  canInvite: boolean;
  refetchCollaborators: () => void;
}) => {
  const { sandboxId } = useShareContext();
  const [errorMessage, setErrorMessage] = useState("");

  const track = useAnalytics();

  const [addCollaboratorResult, addCollaborator] =
    useAddCollaborator(sandboxId);
  const [inviteCollaboratorResult, inviteCollaborator] =
    useInviteCollaborator(sandboxId);

  const loading =
    addCollaboratorResult.fetching || inviteCollaboratorResult.fetching;

  const [search, setSearch] = useState("");

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: userSearchData } = useSearchUsersByName(search);

  const error = addCollaboratorResult.error || inviteCollaboratorResult.error;
  useEffect(() => {
    if (!error) {
      return;
    }

    if (error.message.includes("Could not find user with username")) {
      setErrorMessage("We could not find an user with this username or email");
    } else if (
      error.message.includes("This user is already a collaborator of")
    ) {
      setErrorMessage("This user is already a collaborator of this project.");
    }
  }, [error]);

  const onInvite = async (value: { username: string } | { email: string }) => {
    if ("email" in value) {
      track("invite_collaborator_email", {
        authorization: Authorization.Read.toString(),
      });
      await inviteCollaborator({
        email: value.email,
        authorization: gql.Authorization.READ,
      });
    } else {
      track("invite_collaborator_username", {
        authorization: Authorization.Read.toString(),
      });
      await addCollaborator({
        username: value.username,
        authorization: gql.Authorization.READ,
      });
    }

    setErrorMessage("");
    await refetchCollaborators();

    setSearch("");
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const search = e.target[0].value as string;

        const isEmail = search.includes("@");
        // if it matches an email regexp
        if (isEmail) {
          onInvite({ email: search });
        } else {
          onInvite({
            username: search,
          });
        }
      }}
    >
      <Stack gap={0} horizontal>
        <Box css={{ flex: 1 }}>
          <InputWithSuggestions<{ value: string; avatarSrc: string }>
            disabled={!canInvite}
            inputCss={{
              width: "100%",
              height: "$7",
              border: "1px solid $neutral-bg-high",
              borderRadius: "$2 0 0 $2",
              borderRight: "none",
            }}
            items={
              userSearchData?.map((u) => ({
                value: u.username,
                avatarSrc: u.avatar_url,
              })) ?? []
            }
            onInputValueChange={(value) => setSearch(value)}
            onSuggestionSelected={({ value }) => {
              if (!value) return;

              onInvite({ username: value });
              setSearch("");
            }}
            placeholder="Add collaborator username or email"
            value={search}
          >
            {({ value, avatarSrc }) => (
              <>
                <Avatar name={value} shape="circle" size="s" src={avatarSrc} />
                <Text color="neutral-fg-high">{value}</Text>
              </>
            )}
          </InputWithSuggestions>
        </Box>
        <Button
          css={{ borderRadius: "0 $2 $2 0" }}
          disabled={!canInvite}
          icon="people"
          loading={loading}
          type="submit"
          variant="secondary"
        >
          Invite
        </Button>
      </Stack>

      {errorMessage && (
        <Text
          css={{
            display: "block",
            marginTop: "$1",
            color: "$informative-error-base",
          }}
        >
          {errorMessage}
        </Text>
      )}
    </form>
  );
};
